/**
 * OptiNetwork Dashboard — Stage 2 landing/hub page.
 *
 * Replaces the bare map page as the user-facing entry point for the
 * OptiNetwork product. Renders a grid of cards linking to the existing
 * OptiNetwork surfaces (map, company dashboard, cross-shop intelligence,
 * recommendations, talent, training, governance) — each gated by the
 * same visibility predicates the top-bar OptiNetwork dropdown already
 * uses (`canViewOrgDashboard` / `canViewInScopeTalent` from
 * `useEnterpriseScope`).
 *
 * This page is read-only navigation only. No mutations, no data fetches,
 * no edge-function calls.
 *
 * Internal-naming note: the existing map component is still exported
 * as `FlooNetworkMap` and lives under `src/pages/admin/FlooNetworkMap.tsx`
 * for Stage 1 compatibility. Routes below point at
 * `/admin/optinetwork/map` which mounts that same component via App.tsx.
 */

import { useNavigate } from 'react-router-dom';
import {
  Network,
  Building2,
  Activity,
  Sparkles,
  Users,
  TrendingUp,
  GraduationCap,
  Compass,
  Shield,
  ArrowLeft,
} from 'lucide-react';

import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface HubCard {
  testId: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  visible: boolean;
}

const OptiNetworkDashboard = () => {
  const navigate = useNavigate();
  const { canViewOrgDashboard, canViewInScopeTalent } = useEnterpriseScope();

  const cards: HubCard[] = [
    {
      testId: 'optinetwork-card-map',
      title: 'OptiNetwork Map',
      description:
        'View and (for owners and organization admins) build your organization → region → district → shop hierarchy.',
      icon: Network,
      to: '/admin/optinetwork/map',
      visible: canViewOrgDashboard,
    },
    {
      testId: 'optinetwork-card-company',
      title: 'Company Dashboard',
      description:
        'Organization-wide active tickets, completed work, capacity, and per-shop performance.',
      icon: Building2,
      to: '/org',
      visible: canViewOrgDashboard,
    },
    {
      testId: 'optinetwork-card-intelligence',
      title: 'Cross-Shop Intelligence',
      description:
        'Comparative trends, signals, and outliers across the shops in your active OptiNetwork scope.',
      icon: Activity,
      to: '/org/intelligence',
      visible: canViewOrgDashboard,
    },
    {
      testId: 'optinetwork-card-recommendations',
      title: 'Recommendations',
      description:
        'Suggested operational improvements derived from your network activity.',
      icon: Sparkles,
      to: '/org/recommendations',
      visible: canViewOrgDashboard,
    },
    {
      testId: 'optinetwork-card-talent',
      title: 'Talent Directory',
      description:
        'Browse technicians, advisors, and managers visible to your active scope.',
      icon: Users,
      to: '/enterprise/talent',
      visible: canViewInScopeTalent,
    },
    {
      testId: 'optinetwork-card-talent-analytics',
      title: 'Talent Analytics',
      description:
        'Aggregate performance, efficiency, and trend metrics for in-scope talent.',
      icon: TrendingUp,
      to: '/enterprise/talent/analytics',
      visible: canViewInScopeTalent,
    },
    {
      testId: 'optinetwork-card-training',
      title: 'Training Visibility',
      description:
        'Recent training activity, completions, and gaps for in-scope talent.',
      icon: GraduationCap,
      to: '/enterprise/training',
      visible: canViewInScopeTalent,
    },
    {
      testId: 'optinetwork-card-talent-suggestions',
      title: 'Talent Fit Suggestions',
      description:
        'AI-assisted suggestions for talent fit and cross-shop coverage.',
      icon: Compass,
      to: '/enterprise/talent/suggestions',
      visible: canViewInScopeTalent,
    },
    {
      testId: 'optinetwork-card-governance',
      title: 'Intelligence Governance',
      description:
        'Audit trail, retention, and policy controls for OptiNetwork intelligence surfaces.',
      icon: Shield,
      to: '/enterprise/intelligence/governance',
      visible: canViewOrgDashboard,
    },
  ];

  const visibleCards = cards.filter((c) => c.visible);

  // Stage 2 — hub "Back" affordance. Prefers browser history when
  // available (covers the account-menu → hub → back-to-app path) and
  // falls back to the app's root dayboard route. We probe history.state
  // rather than history.length because length includes the initial
  // SPA entry and over-counts for direct-load cases.
  const handleHubBack = () => {
    if (typeof window !== 'undefined' && window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  return (
    <div
      className="container mx-auto py-6 px-4 max-w-5xl"
      data-testid="optinetwork-dashboard"
    >
      <div className="mb-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleHubBack}
          className="gap-1"
          data-testid="opti-network-hub-back"
        >
          <ArrowLeft className="h-3 w-3" />
          Back
        </Button>
      </div>
      <header className="flex items-start gap-3 mb-6">
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <Network className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">OptiNetwork</h1>
          <p className="text-sm text-muted-foreground">
            Manage your company structure, visibility, talent, training, and
            cross-shop intelligence.
          </p>
        </div>
      </header>

      {visibleCards.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Your account does not currently have access to any OptiNetwork
            surfaces. Contact an owner or organization admin if you believe
            this is unexpected.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {visibleCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.testId}
                className="flex flex-col"
                data-testid={card.testId}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Icon className="h-4 w-4 text-primary" />
                    {card.title}
                  </CardTitle>
                  <CardDescription>{card.description}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(card.to)}
                    data-testid={`${card.testId}-open`}
                  >
                    Open
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OptiNetworkDashboard;
